import React, { useEffect, useState, memo } from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Toolbar, AppBar, IconButton, CssBaseline, Divider, Box, Menu, MenuItem, Tooltip } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ChevronLeftIcon from '@mui/icons-material/ChevronRight';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import { useDevice } from 'components/DeviceContext.js';
import Logo from 'imagens/logo-drogas-2.png';
import authService from 'services/authService';

const drawerWidthExpanded = 180;
const drawerWidthCollapsed = 50;

const Sidebar = () => {
  const [open, setOpen] = useState(true);
  const [profile, setProfile] = useState(null);
  const { isMobile, isTablet } = useDevice();
  const location = useLocation();
  const navigate = useNavigate(); 
  const [anchorEl, setAnchorEl] = useState(null);

  // Função para abrir o menu de configurações
  const handleSettingsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Função para fechar o menu de configurações
  const handleSettingsClose = () => {
    setAnchorEl(null);
  };

  // Função para redirecionar para a página de perfil
  const handleViewProfile = () => {
    navigate('/admin/perfil');
    handleSettingsClose();
  };

  // Função para logout
  const handleLogout = () => {
    authService.logout();
    navigate('/');
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userProfile = await authService.getProfile();
        setProfile(userProfile);
      } catch (error) {
        console.error('Erro ao buscar perfil:', error);
      }
    };
    fetchUserProfile();
  }, []);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const getMenuItemStyles = (open) => ({
    marginBottom: 1.5,
    fontSize: '1rem',
    justifyContent: open ? 'initial' : 'center',
    '& .MuiListItemIcon-root': {
      minWidth: open ? 0 : 'auto',
    },
    '& .MuiListItemText-root': {
      display: open ? 'block' : 'none',
    },
  });

  if (profile === null) {
    return <div>Carregando...</div>;
  }

  const isAdmin = profile.perfil === 1 || profile.perfil === 2 || profile.perfil === 3;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'var(--azul-escuro)',
          transition: (theme) =>
            theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={toggleDrawer}
            sx={{ marginRight: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>
            <img src={Logo} width="190px" alt="Logo" style={{ padding: '0.6em' }} />
          </Link>
        </Toolbar>
      </AppBar>

      {/* Ícone de configurações flutuante */}
      <IconButton
        sx={{
          position: 'fixed',
          bottom: (theme) => theme.spacing(2),
          left: (theme) => theme.spacing(1),
          zIndex: 1300,
        }}
        aria-controls="settings-menu"
        aria-haspopup="true"
        onClick={handleSettingsClick}
      >
        <SettingsIcon />
      </IconButton>

      {/* Menu de configurações */}
      <Menu
        id="settings-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleSettingsClose}
      >
        <MenuItem onClick={handleViewProfile}>Ver Perfil</MenuItem>
        <MenuItem onClick={handleLogout}>Sair</MenuItem>
      </Menu>

      <Drawer
        variant={(isMobile || isTablet) ? 'temporary' : 'permanent'}
        open={open}
        onClose={toggleDrawer}
        sx={{
          width: open ? drawerWidthExpanded : drawerWidthCollapsed,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: open ? drawerWidthExpanded : drawerWidthCollapsed,
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            boxSizing: 'border-box',
            overflowX: 'hidden',
          },
        }}
      >
        <Toolbar>
          <IconButton onClick={toggleDrawer}>
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Toolbar>
        <Divider />
        <List>
          {/* Tooltip só aparece quando o Drawer está recolhido */}
          {!open ? (
            <Tooltip title="Principal" placement="right">
              <ListItem
                button
                component={Link}
                to="/admin/principal"
                sx={getMenuItemStyles(open)}
                selected={location.pathname === '/admin/principal'}
              >
                <ListItemIcon>
                  <RestartAltOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Principal" />
              </ListItem>
            </Tooltip>
          ) : (
            <ListItem
              button
              component={Link}
              to="/admin/principal"
              sx={getMenuItemStyles(open)}
              selected={location.pathname === '/admin/principal'}
            >
              <ListItemIcon>
                <RestartAltOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Principal" />
            </ListItem>
          )}

          {isAdmin && profile.perfil === 3 && (
            !open ? (
              <Tooltip title="Usuários" placement="right">
                <ListItem
                  button
                  component={Link}
                  to="/admin/usuarios"
                  sx={getMenuItemStyles(open)}
                  selected={location.pathname === '/admin/usuarios'}
                >
                  <ListItemIcon>
                    <PeopleAltIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Usuários" />
                </ListItem>
              </Tooltip>
            ) : (
              <ListItem
                button
                component={Link}
                to="/admin/usuarios"
                sx={getMenuItemStyles(open)}
                selected={location.pathname === '/admin/usuarios'}
              >
                <ListItemIcon>
                  <PeopleAltIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Usuários" />
              </ListItem>
            )
          )}

          {isAdmin && profile.perfil === 3 && (
            !open ? (
              <Tooltip title="Solicitações" placement="right">
                <ListItem
                  button
                  component={Link}
                  to="/admin/solicitacoes"
                  sx={getMenuItemStyles(open)}
                  selected={location.pathname === '/admin/solicitacoes'}
                >
                  <ListItemIcon>
                    <AssignmentTurnedInIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Solicitações" />
                </ListItem>
              </Tooltip>
            ) : (
              <ListItem
                button
                component={Link}
                to="/admin/solicitacoes"
                sx={getMenuItemStyles(open)}
                selected={location.pathname === '/admin/solicitacoes'}
              >
                <ListItemIcon>
                  <AssignmentTurnedInIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Solicitações" />
              </ListItem>
            )
          )}

          {isAdmin && profile.perfil === 3 && (
            !open ? (
              <Tooltip title="Questionário" placement="right">
                <ListItem
                  button
                  component={Link}
                  to="/admin/questionario"
                  sx={getMenuItemStyles(open)}
                  selected={location.pathname === '/admin/questionario'}
                >
                  <ListItemIcon>
                    <LiveHelpIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Questionário" />
                </ListItem>
              </Tooltip>
            ) : (
              <ListItem
                button
                component={Link}
                to="/admin/questionario"
                sx={getMenuItemStyles(open)}
                selected={location.pathname === '/admin/questionario'}
              >
                <ListItemIcon>
                  <LiveHelpIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Questionário" />
              </ListItem>
            )
          )}

          {profile.perfil === 1 && (
            !open ? (
              <Tooltip title="Pesquisa" placement="right">
                <ListItem
                  button
                  component={Link}
                  to="/admin/pesquisa"
                  sx={getMenuItemStyles(open)}
                  selected={location.pathname === '/admin/pesquisa'}
                >
                  <ListItemIcon>
                    <SearchIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Pesquisa" />
                </ListItem>
              </Tooltip>
            ) : (
              <ListItem
                button
                component={Link}
                to="/admin/pesquisa"
                sx={getMenuItemStyles(open)}
                selected={location.pathname === '/admin/pesquisa'}
              >
                <ListItemIcon>
                  <SearchIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Pesquisa" />
              </ListItem>
            )
          )}
        </List>
      </Drawer>
    </Box>
  );
};

export default memo(Sidebar);
